import { FC, useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  type LabelProps,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis
} from 'recharts';
import type { MonthlyStageRow } from '../forecasting.types';
import { STAGE_COLORS } from '../forecastingTheme';
import { formatCurrencyCompact, formatCurrencyPrecise } from '../utils/forecastingAggregations';
import { ChartPlot, ChartTitle, Legend, LegendButton, Panel } from '../Forecasting.styles';
import { currencyAxisProps, maxOf } from '../utils/currencyAxis';
import { useShellPalette } from '@/styles';

interface MonthlyStackedChartProps {
  data: MonthlyStageRow[];
  chartTitle: string;
  selectedMonthKey: string | null;
  onMonthSelect: (monthKey: string | null) => void;
  variant?: 'dynamic' | 'budget';
}

/** Looker legend order: Forecast, Booked, Pipe (stack bottom → top: Booked, Pipe, Forecast). */
const LEGEND_STAGES = ['Forecast', 'Booked', 'Pipe'] as const;
const STACK_STAGES = ['Booked', 'Pipe', 'Forecast'] as const;
/** The budget view draws a single series. */
const BUDGET_STAGES = ['Booked'] as const;
type StageKey = (typeof STACK_STAGES)[number];

type MonthlyStackedTooltipProps = TooltipProps<number, string> & {
  payload?: Array<{ payload: MonthlyStageRow }>;
  variant?: 'dynamic' | 'budget';
  /** Stages drawn, so a stage hidden from the legend is not listed either. */
  stages?: readonly StageKey[];
};

function MonthlyStackedTooltip({
  active,
  payload,
  variant = 'dynamic',
  stages: shownStages = STACK_STAGES
}: MonthlyStackedTooltipProps) {
  if (!active || !payload?.length) return null;

  const row = payload[0].payload as MonthlyStageRow;
  const stages = (variant === 'budget' ? (['Booked'] as const) : shownStages)
    .map((stageKey) => ({
      stage: variant === 'budget' ? 'Budget' : stageKey,
      value: Number(row[stageKey]) || 0
    }))
    .filter(({ value }) => value !== 0);

  if (stages.length === 0) return null;

  return (
    <div
      style={{
        background: '#ffffff',
        color: '#333333',
        padding: '10px 14px',
        borderRadius: 4,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.18)',
        fontSize: 13,
        lineHeight: 1.5,
        minWidth: 180
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 8, color: '#1a1a1a' }}>{row.axisLabel}</div>
      {stages.map(({ stage, value }) => (
        <div
          key={stage}
          style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}
        >
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 2,
              background: stage === 'Budget' ? STAGE_COLORS.Booked : STAGE_COLORS[stage as StageKey],
              flexShrink: 0
            }}
          />
          <span>
            {stage}: {formatCurrencyPrecise(value)}
          </span>
        </div>
      ))}
      <div style={{ marginTop: 8, fontSize: 11, color: '#666' }}>Click to filter breakdown below</div>
    </div>
  );
}

/** Zero out sub-pixel / rounding noise so Recharts does not paint forecast slivers on booked bars. */
function sanitizeMonthlyChartData(data: MonthlyStageRow[]): MonthlyStageRow[] {
  const minVisible = 1;

  return data.map((row) => {
    const Forecast = (Number(row.Forecast) || 0) < minVisible ? 0 : Number(row.Forecast) || 0;
    const Booked = (Number(row.Booked) || 0) < minVisible ? 0 : Number(row.Booked) || 0;
    const Pipe = (Number(row.Pipe) || 0) < minVisible ? 0 : Number(row.Pipe) || 0;

    return {
      ...row,
      Forecast,
      Booked,
      Pipe,
      Other: 0,
      total: Forecast + Booked + Pipe
    };
  });
}

function monthOpacity(monthKey: string, selectedMonthKey: string | null): number {
  if (!selectedMonthKey) return 1;
  return monthKey === selectedMonthKey ? 1 : 0.35;
}

export const MonthlyStackedChart: FC<MonthlyStackedChartProps> = ({
  data,
  chartTitle,
  selectedMonthKey,
  onMonthSelect,
  variant = 'dynamic'
}) => {
  /* Resolved colours, not CSS variables: Recharts writes these into SVG
     attributes, where var() is not reliably supported. */
  const shell = useShellPalette();
  /* Stages picked from the legend, Looker-style: empty shows all three, and
     clicking one narrows the chart to it (click more to add, again to drop). */
  const [pickedStages, setPickedStages] = useState<StageKey[]>([]);
  useEffect(() => setPickedStages([]), [variant]);

  const stackStages = useMemo<readonly StageKey[]>(() => {
    if (variant === 'budget') return BUDGET_STAGES;
    return pickedStages.length ? STACK_STAGES.filter((stage) => pickedStages.includes(stage)) : STACK_STAGES;
  }, [variant, pickedStages]);

  const togglePickedStage = (stage: StageKey) =>
    setPickedStages((current) =>
      current.includes(stage) ? current.filter((s) => s !== stage) : [...current, stage]
    );
  /* labelTotal sums only the stages drawn, so the figure on top of a bar is the
     height of that bar (the budget view draws one series). labelStage is the
     topmost stage with a value: the total sits above it, as Recharts draws no
     label on a zero-height segment. */
  const chartData = useMemo(
    () =>
      sanitizeMonthlyChartData(data).map((row) => ({
        ...row,
        labelTotal: stackStages.reduce((sum, stage) => sum + (Number(row[stage]) || 0), 0),
        labelStage: [...stackStages].reverse().find((stage) => (Number(row[stage]) || 0) > 0)
      })),
    [data, stackStages]
  );

  const rowsByMonth = useMemo(() => new Map(chartData.map((row) => [row.monthKey, row])), [chartData]);

  /** Draws the month's total above `stage`, for the months where it is the top segment.
   *  The month comes from the label's value (see valueAccessor below), not its
   *  index: Recharts indexes labels among a series' non-empty bars only. */
  const renderTotalLabel =
    (stage: (typeof STACK_STAGES)[number]) =>
    ({ value, x, y, width }: LabelProps) => {
      const row = rowsByMonth.get(String(value));
      if (!row || row.labelStage !== stage) return null;
      return (
        <text
          x={Number(x) + Number(width) / 2}
          y={Number(y) - 6}
          textAnchor="middle"
          fill={shell.dataText}
          fontSize={10}
          fontWeight={600}
        >
          {formatCurrencyCompact(row.labelTotal)}
        </text>
      );
    };
  const legendItems =
    variant === 'budget'
      ? [{ key: 'Budget', color: STAGE_COLORS.Booked }]
      : LEGEND_STAGES.map((s) => ({ key: s, color: STAGE_COLORS[s] }));
  const legendSelectable = variant !== 'budget';

  const handleMonthClick = (monthKey: string) => {
    onMonthSelect(selectedMonthKey === monthKey ? null : monthKey);
  };

  return (
    <Panel>
      <ChartTitle>{chartTitle}</ChartTitle>
      <Legend>
        {legendItems.map(({ key, color }) =>
          legendSelectable ? (
            <LegendButton
              key={key}
              type="button"
              aria-pressed={pickedStages.includes(key as StageKey)}
              $dimmed={pickedStages.length > 0 && !pickedStages.includes(key as StageKey)}
              onClick={() => togglePickedStage(key as StageKey)}
            >
              <i style={{ background: color }} />
              {key}
            </LegendButton>
          ) : (
            <span key={key}>
              <i style={{ background: color }} />
              {key}
            </span>
          )
        )}
      </Legend>
      <ChartPlot>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart
            data={chartData}
            margin={{ top: 22, right: 12, left: 4, bottom: 8 }}
            accessibilityLayer={false}
          >
            <CartesianGrid stroke={shell.chartGrid} vertical={false} />
            <XAxis
              dataKey="axisLabel"
              /* Upright: "Apr-26" is short enough for twelve months to sit
                 side by side, where "1 Apr 2026" needed tilting. */
              height={28}
              tick={{ fill: shell.dataText, fontSize: 10, fontWeight: 400 }}
              axisLine={{ stroke: shell.chartAxisLine }}
              tickLine={false}
              interval={0}
            />
            <YAxis
              tick={{ fill: shell.dataText, fontSize: 11, fontWeight: 400 }}
              axisLine={false}
              tickLine={false}
              {...currencyAxisProps(maxOf(chartData, ['labelTotal']))}
              width={52}
            />
            <Tooltip
              content={<MonthlyStackedTooltip variant={variant} stages={stackStages} />}
              cursor={{ fill: shell.menuHoverBg }}
              shared={false}
            />
            {stackStages.map((stage, index) => (
              <Bar
                key={stage}
                name={variant === 'budget' ? 'Budget' : stage}
                dataKey={stage}
                stackId="stages"
                fill={STAGE_COLORS[stage as StageKey]}
                radius={index === stackStages.length - 1 ? [2, 2, 0, 0] : [0, 0, 0, 0]}
                isAnimationActive={false}
              >
                {chartData.map((entry) => (
                  <Cell
                    key={`${stage}-${entry.monthKey}`}
                    fillOpacity={monthOpacity(entry.monthKey, selectedMonthKey)}
                    cursor="pointer"
                    onClick={() => handleMonthClick(entry.monthKey)}
                  />
                ))}
                {/* The month's total, once, above whichever segment is on top. */}
                <LabelList
                  valueAccessor={(entry: { payload?: { monthKey?: string } }) => entry?.payload?.monthKey}
                  content={renderTotalLabel(stage)}
                />
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </ChartPlot>
    </Panel>
  );
};
